import { useEffect } from "react";

/**
 * Run `callback` when the authenticated uid actually CHANGES — never on a
 * plain remount.
 *
 * The naive `useEffect(cb, [uid])` fires on every fresh mount of the
 * consuming component, so a query hook that clears + refetches "after user
 * change" that way really refetches on every navigation to the screen
 * (2026-08-04: explore/music served brand-new recommendations on every
 * visit, defeating staleTime AND burning the engine's anti-repeat pool —
 * each serve is recorded server-side). Each factory instance keeps the
 * last-seen uid in module scope, so it survives remounts; the first mount
 * of a JS session only RECORDS the uid (the durable cache + staleTime
 * govern freshness there), and concurrent consumers of the same instance
 * dedupe to a single callback per transition.
 *
 * `undefined` means Firebase has not reported the identity yet (a cold start
 * before it restores the saved user) and is neither recorded nor a change;
 * pass `null` for signed out.
 */
export function makeOnUserChange() {
  let lastUid: string | null | undefined;
  return function useOnUserChange(
    uid: string | null | undefined,
    callback: () => void,
  ) {
    useEffect(() => {
      if (uid === undefined) return;
      if (lastUid === undefined) {
        lastUid = uid;
        return;
      }
      if (lastUid === uid) return;
      lastUid = uid;
      callback();
      // The callback identity is deliberately not a dependency — it is a
      // fire-on-transition action, not a synchronized subscription.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uid]);
  };
}
